import Close from '@mui/icons-material/Close'
import { Box, Divider, Grid } from '@mui/material'
import { MdFilterList } from 'react-icons/md'
import {
  Button,
  Card,
  DateRangePickerC,
  IconButton,
  Modal,
  SelectC,
  ResetButton,
  ResetFilters,
  CardHeader
} from 'components'

import { Yup, existsFilters } from 'utils'
import { useOrderListContext } from '../context/orderList.context'
import { useLoadOrdersStatus } from 'modules/orders/hooks'
import {
  SelectBusinessUnity,
  SelectCustomer,
  SelectSeller
} from 'modules/orders/components'

import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useOrderStore } from 'modules/orders/store'
import { useEffect } from 'react'

type FormValues = {
  status: { id: number; name: string } | null
  seller: {
    id: string
    name: string
  } | null
  customerId: {
    id: string
  } | null
  businessUnity: {
    id: string
  } | null
  date: string[] | null[]
  listMode: string
}

const schema = Yup.object({
  status: Yup.object({
    id: Yup.string(),
    label: Yup.string(),
    initials: Yup.string()
  }).nullable(),
  date: Yup.mixed().test({
    name: 'dateRange',
    message: 'Deve conter data de início e fim!',
    test: (value) => {
      const isEmpty = value.every((e: any) => e === null)
      if (isEmpty) return isEmpty
      return value.every((e: any) => e !== null)
    }
  }),
  seller: Yup.object({
    id: Yup.string()
  }).nullable(),
  customerId: Yup.object({
    id: Yup.string()
  }).nullable(),
  businessUnity: Yup.object({
    id: Yup.string()
  }).nullable(),
  listMode: Yup.string()
})

const defaultValues = {
  status: null,
  date: [null, null],
  seller: null,
  customerId: null,
  businessUnity: null,
  listMode: ''
}

export const FilterListOrder = () => {
  const ctx = useOrderListContext()
  const { showOrderFilter, setShowOrderFilter } = ctx

  const setFiltersForm = useOrderStore.useSetFiltersForm()
  const filtersForm = useOrderStore.useFiltersForm()
  const setFilters = useOrderStore.useSetFilters()
  const filters = useOrderStore.useFilters()

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  })

  const { handleSubmit, setValue } = methods

  const { data: controls, isLoading: isControlLoading } = useLoadOrdersStatus(
    {}
  )

  const onSubmit = (data: FormValues) => {
    const dateRange =
      data?.date.length === 2 && !data.date.some((e: any) => e === null)
        ? {
            startDate: data.date[0],
            endDate: data.date[1]
          }
        : undefined

    const normalizedData = {
      statusId: data?.status?.id,
      ...dateRange,
      dateRange,
      sellerId: data?.seller?.id,
      customerId: data?.customerId?.id,
      businessUnity: data?.businessUnity?.id,
      listMode: filters?.listMode
    }
    setFilters(normalizedData)
    setFiltersForm(data)
    setShowOrderFilter(false)
  }

  useEffect(() => {
    setValue('status', filtersForm.status)
    setValue('seller', filtersForm.seller)
    setValue('customerId', filtersForm.customerId)
    setValue('businessUnity', filtersForm.businessUnity)
    setValue('date', filtersForm.date)
  }, [filtersForm, setValue])

  return (
    <FormProvider {...methods}>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Button
          sx={{ display: { md: 'flex', sm: 'none', xs: 'none' } }}
          variant="text"
          style={{ marginRight: '10px' }}
          startIcon={<MdFilterList size={18} />}
          onClick={() => setShowOrderFilter(true)}
        >
          Filtros
        </Button>
        <IconButton
          sx={{ display: { md: 'none' } }}
          onClick={() => setShowOrderFilter(true)}
        >
          <MdFilterList size={24} color="#023770" />
        </IconButton>
        <Box>
          {existsFilters(filters) && (
            <ResetFilters
              onClick={() => {
                setFilters({
                  listMode: 'proposals'
                })
              }}
            />
          )}
        </Box>
      </Box>
      <Modal
        open={showOrderFilter}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }}
        onClose={() => setShowOrderFilter(false)}
      >
        <Card
          sx={{
            width: { xs: 300, sm: 400 },
            padding: { xs: 0 }
          }}
        >
          <CardHeader
            title="Filtros"
            HeaderRight={
              <IconButton onClick={() => setShowOrderFilter(false)}>
                <Close />
              </IconButton>
            }
          />
          <Divider sx={{ marginBottom: 1 }} />
          <Grid container spacing={2} p={2}>
            <Grid item xs={12}>
              <SelectC
                name="status"
                getOptionLabel={(option: any) => option?.name}
                options={controls?.data || []}
                label="Situação"
                loading={isControlLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <SelectSeller />
            </Grid>
            <Grid item xs={12}>
              <SelectCustomer />
            </Grid>
            <Grid item xs={12}>
              <SelectBusinessUnity />
            </Grid>
            <Grid item xs={12}>
              <DateRangePickerC
                name="date"
                inputFormat="dd/MM/yyyy"
                startText="Data Inicial"
                endText="Data Final"
              />
            </Grid>
          </Grid>
          <Divider sx={{ marginTop: 1 }} />
          <Grid
            container
            item
            alignItems="center"
            justifyContent="space-between"
            xs={12}
            p={2}
          >
            <ResetButton
              onClick={() => {
                setFilters({
                  listMode: 'proposals'
                })
              }}
            />
            <Button
              type="button"
              variant="contained"
              onClick={handleSubmit(onSubmit)}
            >
              Aplicar
            </Button>
          </Grid>
        </Card>
      </Modal>
    </FormProvider>
  )
}
